import getConfig from 'next/config';
import goFetch from 'isomorphic-unfetch';
import Cors from 'cors';

const { publicRuntimeConfig } = getConfig();

export async function fetch (url, options) {
  const finalUrl = url.startsWith('http')
    ? url
    : publicRuntimeConfig.SERVER_ADDR
      .replace(/\/$/, '') + '/' + url.replace(/^(\.|\/)/g, '');

  return new Promise((resolve, reject) => {
    goFetch(finalUrl, options)
      .then(async data => {
        resolve({ data: await data.json() });
      })
      .catch(error => {
        resolve({error});
      });
  });
}

// Helper method to wait for a middleware to execute before continuing
// And to throw an error when an error happens in a middleware
function runMiddleware (req, res, fn) {
  return new Promise((resolve, reject) => {
    fn(req, res, (result) => {
      if (result instanceof Error) {
        return reject(result);
      }

      return resolve(result);
    });
  });
}

export function initMiddleware (middleware) {
  return (req, res) =>
    new Promise((resolve, reject) => {
      middleware(req, res, (result) => {
        if (result instanceof Error) {
          return reject(result);
        }
        return resolve(result);
      });
    });
}

export async function applyCors (req, res, options) {
  const cors = Cors(options);

  return await runMiddleware(req, res, cors);
}

export function notAllowed (res) {
  return res
    .status(405)
    .send({
      error: { message: 'Not allowed' },
      data: null
    });
}

export function notFound (res) {
  return res
    .status(404)
    .send({
      error: { message: 'Not found' },
      data: null
    });
}

export function abort (res, status = '400', message = 'Bad Request') {
  return res.status(status).send({ message });
}

export function send (res, data = {}, status = 200) {
  res.statusCode = status;
  try {
    res.setHeader('Content-Type', 'application/json');
  } catch (error) {
    // do nothing
  }
  res.end(JSON.stringify(data));
}

export async function fetchJson (...args) {
  try {
    const response = await goFetch(...args);
    const contentType = response.headers.get('content-type');

    let data;
    if (contentType && contentType.indexOf('application/json') !== -1) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    if (response.ok) {
      return data;
    }

    const error = new Error(response.statusText);
    error.response = response;
    error.data = data;
    throw error;
  } catch (error) {
    if (!error?.data) {
      error.data = { message: error.message };
    }
    throw error;
  }
}

export async function postJSON (url, data, options = {}) {
  data = typeof data === 'object' ? JSON.stringify(data) : data;

  try {
    const result = await fetchJson(url, {
      method: 'POST',
      body: data,
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {})
      },
    });

    return {
      ...result,
      data: result?.data || null,
      error: result.error || null
    };
  } catch (error) {
    return {
      data: null,
      error
    };
  }
}

export async function uploadFile (file, bucket, name) {
  const formData = new FormData();
  formData.append('type', 'file');
  formData.append('file', file);

  return fetch(`/api/upload/${bucket}/${name}`, {
    method: 'POST',
    body: formData
  }).then(
    response => {
      if (response.json) {
        return response.json()?.data;
      }

      return response.text
        ? response.text()
        : response?.data ||response || null;
    }
  );
}

export async function sendFileToStorageDrive (file, data) {
  const formData = new FormData();
  // formData.append('type', 'file');
  formData.set('file', file);
  // formData.append('name', data.name);
  // formData.append('folder', folder);

  for (const key in data) {
    if (Object.hasOwnProperty.call(data, key)) {
      let value = data[key];
      if (value === undefined) {
        formData.set(key, null);
      } else {
        formData.set(key, JSON.stringify(value));
      }
    }
  }

  return fetch(`/api/storage-drive/upsert`, {
    method: 'POST',
    body: formData
  }).then(
    response => {
      if (response.json) {
        return response.json()?.data;
      }

      return response.text
        ? response.text()
        : response?.data || response || null;
    }
  ).catch(error => {
    return { error };
  });
}

/**
 * Create a fetch request with a timeout, based on AbortController and AbortSignal
 *
 * @param {string} url Endpoint
 * @param {integer} ms Timeout in miliseconds
 * @param {*} param2 Custom fetch parameters
 * @returns Promise with a timeout
 */
export function fetchTimeout (url, ms = 5000, { signal, ...options } = {}) {
  const finalUrl = url.startsWith("http")
    ? url
    : publicRuntimeConfig
      .SERVER_ADDR
      .replace(/\/$/, '') + '/' + url.replace(/^(\.|\/)/g, '');

  const controller = new AbortController();
  const promise = fetch(finalUrl, { signal: controller.signal, ...options });
  if (signal) {
    signal.addEventListener("abort", () => controller.abort());
  }

  const timeout = setTimeout(() => {
    console.error(`ERRO: Timed out request after ${ms} miliseconds.`);
    return controller.abort();
  }, parseInt(ms, 10));

  return promise
    .then(async res => {
      const status = parseInt(res.status, 10);
      const contentType = res.headers.get('content-type');

      let body;
      if (contentType && contentType.indexOf('application/json') !== -1) {
        body = await res.json();
      } else {
        body = await res.text();
      }

      if (status >= 400) {
        // Ignora do log erro normal de expiração de token
        if (body?.error !== 'invalid_token') {
          console.error(
            `Fetchtimeout Error: Response from '${url}': ${res.statusText}`
          );
          console.info(`Content-Type da resposta: '${contentType}'`);
          console.info(res);
          console.debug(body);
        }

        throw new Error(body?.error || body || res.statusText);
      }

      return body;
    })
    .finally(() => clearTimeout(timeout));
}

export default fetch;
